/**
 * sidebarOrderStore.ts
 *
 * Raw read/write for the `sidebarOrder` object store.
 * Always one record keyed "global".
 *
 * Mirrors every method from rootPagesOrderContext:
 *   privatePagesOrder      → getSidebarOrder().private
 *   publicPagesOrder       → getSidebarOrder().public
 *   workAreaPagesOrder     → getSidebarOrder().workArea
 *   sharedPagesOrder       → getSidebarOrder().shared
 *   templatePagesOrder     → getSidebarOrder().template
 *   sidebarOrder           → getSidebarOrder().sidebar
 */

import { appDB } from "../db/client";
import type { SidebarOrderRecord } from "../db/schema";

// ─── Default empty record ─────────────────────────────────────────

const DEFAULT_SIDEBAR_ORDER: SidebarOrderRecord = {
  id: "global",
  private: [],
  public: [],
  workArea: {},
  shared: [],
  template: [],
  favourites: [],
  sidebar: [],
  isSync: 1 as 1,
  localUpdatedAt: Date.now(),
};

// ─── Read ─────────────────────────────────────────────────────────

/**
 * Get the current sidebar order record.
 * Returns a default empty record if none exists yet.
 */
export async function getSidebarOrder(): Promise<SidebarOrderRecord> {
  const record = await appDB.sidebarOrder.get("global");
  return record ?? DEFAULT_SIDEBAR_ORDER;
}

// ─── Writes ───────────────────────────────────────────────────────

/**
 * Replaces the entire sidebar order record.
 * Used during initial hydration from WorkspaceResponse.
 */
export async function saveSidebarOrder(
  data: Omit<SidebarOrderRecord, "id" | "isSync" | "localUpdatedAt">,
  isSync: 0 | 1 = 1
): Promise<void> {
  await appDB.sidebarOrder.put({
    id: "global",
    ...data,
    isSync,
    localUpdatedAt: Date.now(),
  });
}

/**
 * Partially update specific fields of the sidebar order.
 * Always marks isSync: 0 | 1 = 0 so the watcher picks it up.
 * Used by individual mutation functions (addPrivatePage, reorderPrivate, etc.)
 */
export async function updateSidebarOrder(
  updates: Partial<Omit<SidebarOrderRecord, "id" | "isSync" | "localUpdatedAt">>
): Promise<void> {
  const current = await getSidebarOrder();
  await appDB.sidebarOrder.put({
    ...current,
    ...updates,
    id: "global",
    isSync: 0 as 0,
    localUpdatedAt: Date.now(),
  });
}

/**
 * Mark sidebar order as synced after watcher confirms server write.
 */
export async function markSidebarOrderSynced(): Promise<void> {
  await appDB.sidebarOrder.update("global", {
    isSync: 1 as 1,
  });
}

// ─── Unsynced query (used by watcher) ────────────────────────────

/** Returns true if the sidebar order has unsynced local changes. */
export async function isSidebarOrderUnsynced(): Promise<boolean> {
  const record = await appDB.sidebarOrder.get("global");
  return record ? record.isSync === 0 : false;
}
